/**
 * Add Network Corridor Form Schema
 */

import { z } from 'zod'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import formYaml from './form.yml?raw'

const formConfig = FormConfigLoader.loadFormConfig(formYaml)

export const FORM_CONFIG = formConfig
export const FORM_NAME = formConfig.formName
export const FORM_TITLE = formConfig.formTitle
export const FORM_DESCRIPTION = formConfig.formDescription

export const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
] as const

export const AddNetworkCorridorFormSchema = z.object({
  networkCorridorNumber: z.number().optional(),

  name: z
    .string()
    .min(1, 'Name is required')
    .max(200, 'Name cannot exceed 200 characters'),

  // Optional per backend — only networkCorridorName is required.
  description: z
    .string()
    .optional(),

  state: z
    .enum(US_STATES)
    .optional(),

  countiesCovered: z
    .string()
    .optional(),

  zipcodesCovered: z
    .string()
    .optional(),

  numberOfPotentialSites: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
    z.number()
      .int('Must be a whole number')
      .positive('Must be a positive number')
      .optional()
  ),

  expectedCorridorSize: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
    z.number()
      .positive('Must be a positive number')
      .optional()
  ),

  networkPartner: z
    .array(z.coerce.number())
    .optional()
    .default([]),

  // Optional corridor map upload (single file key from S3)
  corridorMap: z.string().optional(),

  // Optional corridor pictures
  corridorPictures: z
    .array(z.any())
    .max(10, 'You can upload up to 10 pictures')
    .optional()
    .default([]),
})

export type AddNetworkCorridorFormData = z.infer<typeof AddNetworkCorridorFormSchema>

export function transformToApiRequest(formData: AddNetworkCorridorFormData) {
  const allMediaLocations = (formData.corridorPictures || [])
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object') {
        const candidate = (item as any).objectKey || (item as any).url || (item as any).path
        return typeof candidate === 'string' ? candidate : ''
      }
      return ''
    })
    .filter((value) => value.length > 0)

  // Strip data URLs — those are mock-mode previews and must never be sent to the
  // backend (they can be several MB and cause 500 / payload-too-large errors).
  // In production the real S3 object keys are plain strings that don't start with "data:".
  const mediaLocations = allMediaLocations.filter((v) => !v.startsWith('data:'))

  const normalizedMapLocations = formData.corridorMap
    ? [formData.corridorMap].filter((v) => !v.startsWith('data:'))
    : []

  return {
    networkCorridorNumber: formData.networkCorridorNumber,
    networkCorridorName: formData.name.trim(),
    description: formData.description?.trim() || undefined,
    state: formData.state,
    countiesCovered: formData.countiesCovered?.trim() || undefined,
    zipcodesCovered: formData.zipcodesCovered?.trim() || undefined,
    partnerOrganizationNumbers: formData.networkPartner || [],
    numberOfPotentialSites: formData.numberOfPotentialSites,
    expectedCorridorSize: formData.expectedCorridorSize,
    mapLocations: normalizedMapLocations,
    mediaLocations,
    // Expose the full list (including data URLs) so the composer can inject them
    // into the query cache for immediate display without involving the backend.
    _allMediaLocations: allMediaLocations,
  }
}
